import { jwtDecode } from "jwt-decode";

/**
 * Authentication utility functions
 * Helper functions for JWT token handling and user extraction
 */

/**
 * Extract user information from JWT token stored in localStorage
 * @returns {object|null} - User information object or null if token invalid
 */
export const extractUserFromToken = () => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      return null;
    }

    const decoded = jwtDecode(token);

    // Based on your curl examples, the JWT contains user_id
    return {
      user_id: decoded.user_id,
      username: decoded.username,
      email: decoded.email,
      role: decoded.role,
      homeworkspace: decoded.homeworkspace,
      default_folder: decoded.default_folder,
      default_project: decoded.default_project,
      default_assistant: decoded.default_assistant,
      facility_id: decoded.facility_id,
      facility_timezone: decoded.facility_timezone,
      api_rate_limit: decoded.api_rate_limit,
    };
  } catch (error) {
    console.error("Error extracting user from token:", error);
    // If token is invalid, remove it
    localStorage.removeItem("token");
    return null;
  }
};

/**
 * Get current user ID from token
 * @returns {string|null} - User ID or null if not available
 */
export const getCurrentUserId = () => {
  const user = extractUserFromToken();
  return user ? user.user_id : null;
};

/**
 * Check if user is authenticated
 * @returns {boolean} - True if user has valid token
 */
export const isAuthenticated = () => {
  const token = localStorage.getItem("token");
  if (!token) return false;

  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;

    // Check if token is expired
    if (decoded.exp < currentTime) {
      localStorage.removeItem("token");
      return false;
    }

    return true;
  } catch (error) {
    localStorage.removeItem("token");
    return false;
  }
};

/**
 * Get authorization header for API calls
 * @returns {object} - Authorization header object
 */
export const getAuthHeader = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};
